import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgxEditorModule, Editor } from 'ngx-editor';
import { AuthService } from '../service/auth/auth.service';
import { RestService } from '../service/rest/rest.service';
import { AdminRestService } from '../service/rest/admin-rest.service';

@Component({
  selector: 'app-admin-news',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, NgxEditorModule],
  templateUrl: './admin-news.component.html',
  styleUrl: './admin-users.component.css'
})
export class AdminNewsComponent implements OnInit {
  sessionId = '';
  content: any[] = [];
  loading = false;
  error = '';
  message = '';
  detailItem: any = null;
  detailForm: any = {};
  detailSaving = false;
  deleteItem: any = null;
  deleteError = '';
  sortColumn = 'createdAt';
  sortDir: 'asc' | 'desc' = 'desc';
  newsTextEditor: Editor | null = null;

  constructor(
    private authService: AuthService,
    private restService: RestService,
    private adminRest: AdminRestService
  ) {}

  async ngOnInit() {
    const user = this.authService.getUser();
    this.sessionId = user?.sid || '';
    this.load();
  }

  async load() {
    if (!this.sessionId) return;
    this.loading = true;
    this.error = '';
    try {
      const list = await this.adminRest.getAdminNews(this.sessionId);
      this.content = list ?? [];
    } catch (e: any) {
      this.error = e?.message || 'Failed to load news';
    } finally {
      this.loading = false;
    }
  }

  openCreate() {
    this.detailItem = { id: null };
    this.detailForm = { headline: '', text: '', active: true };
    this.newsTextEditor = new Editor();
  }

  openEdit(n: any) {
    this.detailItem = n;
    this.detailForm = {
      headline: n.headline ?? '',
      text: n.text ?? '',
      active: !!n.active
    };
    this.newsTextEditor = new Editor();
  }

  closeDetail() {
    this.detailItem = null;
    if (this.newsTextEditor) {
      this.newsTextEditor.destroy();
      this.newsTextEditor = null;
    }
    this.load();
  }

  async save() {
    if (!this.sessionId) return;
    this.detailSaving = true;
    this.error = '';
    try {
      if (this.detailItem?.id == null) {
        if (!this.detailForm.headline?.trim()) {
          this.error = 'Headline is required';
          this.detailSaving = false;
          return;
        }
        await this.adminRest.postAdminNews(this.sessionId, {
          headline: this.detailForm.headline.trim(),
          text: this.detailForm.text ?? '',
          active: this.detailForm.active ? 1 : 0
        });
        this.message = 'News created.';
      } else {
        await this.adminRest.putAdminNews(this.sessionId, this.detailItem.id, {
          headline: this.detailForm.headline?.trim() ?? '',
          text: this.detailForm.text ?? '',
          active: this.detailForm.active ? 1 : 0
        });
        this.message = 'News updated.';
      }
      this.closeDetail();
    } catch (e: any) {
      this.error = e?.message || 'Save failed';
    } finally {
      this.detailSaving = false;
    }
  }

  openDeleteConfirm(n: any) {
    this.deleteItem = n;
    this.deleteError = '';
  }

  closeDeleteConfirm() {
    this.deleteItem = null;
    this.deleteError = '';
  }

  async confirmDelete() {
    if (!this.deleteItem?.id || !this.sessionId) return;
    this.deleteError = '';
    try {
      await this.adminRest.deleteAdminNews(this.sessionId, this.deleteItem.id);
      this.closeDeleteConfirm();
      this.load();
    } catch (e: any) {
      this.deleteError = e?.error?.error || e?.message || 'Delete failed';
    }
  }

  get sortedContent(): any[] {
    const col = this.sortColumn;
    const dir = this.sortDir === 'asc' ? 1 : -1;
    return [...this.content].sort((a, b) => {
      let va: any = a[col];
      let vb: any = b[col];
      if (col === 'headline') {
        va = (va ?? '') + '';
        vb = (vb ?? '') + '';
        return dir * (va.localeCompare(vb, undefined, { sensitivity: 'base' }));
      }
      if (col === 'createdAt') {
        va = (va ?? '') + '';
        vb = (vb ?? '') + '';
        return dir * va.localeCompare(vb);
      }
      if (va == null) va = '';
      if (vb == null) vb = '';
      return dir * (va < vb ? -1 : va > vb ? 1 : 0);
    });
  }

  setSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDir = column === 'createdAt' ? 'desc' : 'asc';
    }
  }

  sortIcon(column: string): string {
    if (this.sortColumn !== column) return 'bi bi-arrow-down-up';
    return this.sortDir === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down';
  }
}
